import { useState } from "react";
import { useAccount } from "wagmi";
import { toast } from "sonner";
import "./CreatePoolForm.css";
import "./CustomTokenOnboardForm.css";

const DEFAULT_DECIMALS = 8;

/** Onboarding fee (e.g. $MORM) for custom token registration — "about A" (Round A). */
const CUSTOM_TOKEN_ONBOARD_FEE_ABOUT_A = 10;

/** Convert human-readable token amount to satoshi string (8-decimal precision). */
function toSatoshi(amount: number, decimals: number): string {
  const factor = 10 ** decimals;
  return Math.floor(Number(amount) * factor).toString();
}

export interface CustomTokenOnboardPayload {
  issuer_address: string;
  ticker: string;
  name?: string;
  decimals: number;
  initial_supply?: string;
  description?: string;
  logo_url?: string;
  signature: string;
  nonce: string;
  timestamp: string;
  deadline?: string;
}

interface CustomTokenOnboardFormProps {
  baseUrl: string;
  onSuccess?: (assetIndex: number) => void;
  onCancel?: () => void;
}

export function CustomTokenOnboardForm({
  baseUrl,
  onSuccess,
  onCancel,
}: CustomTokenOnboardFormProps) {
  const { address } = useAccount();
  const issuerAddress = address ?? "";

  const [ticker, setTicker] = useState("");
  const [name, setName] = useState("");
  const [decimals, setDecimals] = useState(DEFAULT_DECIMALS);
  const [initialSupplyTokens, setInitialSupplyTokens] = useState("");
  const [description, setDescription] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!issuerAddress) {
      toast.error("Connect your wallet to onboard a token.");
      return;
    }

    const tickerTrimmed = ticker.trim().toUpperCase();
    if (!tickerTrimmed) {
      toast.error("Ticker is required.");
      return;
    }

    setSubmitting(true);
    try {
      const timestamp = new Date().toISOString();
      const nonce = Date.now().toString();
      // TODO: Replace with EIP-712 signTypedData once domain/types are defined upstream.
      const signature = "0x";

      const body: CustomTokenOnboardPayload = {
        issuer_address: issuerAddress,
        ticker: tickerTrimmed,
        signature,
        nonce,
        timestamp,
        decimals,
      };

      if (name.trim()) body.name = name.trim();
      if (description.trim()) body.description = description.trim();
      if (logoUrl.trim()) body.logo_url = logoUrl.trim();

      const rawSupply = initialSupplyTokens.trim();
      if (rawSupply) {
        const num = parseFloat(rawSupply);
        if (!Number.isNaN(num) && num > 0) {
          body.initial_supply = toSatoshi(num, decimals);
        }
      }

      const res = await fetch(`${baseUrl.replace(/\/$/, "")}/token/v1/custom`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        const errMsg =
          data?.error ?? data?.message ?? `Onboarding failed (${res.status})`;
        toast.error(errMsg);
        return;
      }

      const assetIndex = data?.asset_index;
      toast.success(
        assetIndex != null
          ? `Token onboarded. Asset index: ${assetIndex}`
          : "Token onboarded successfully."
      );
      onSuccess?.(assetIndex);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Network or unexpected error.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-pool-form screen">
      <header className="form-header">
        {onCancel && (
          <button type="button" className="back-btn" onClick={onCancel}>
            ← Back
          </button>
        )}
        <h1>Onboard Custom Token</h1>
      </header>

      {!issuerAddress ? (
        <div className="custom-token-onboard-connect create-pool-form-inner">
          <p>Connect your wallet to onboard a custom token.</p>
        </div>
      ) : (
        <form
          className="create-pool-form-inner custom-token-onboard-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <p className="custom-token-onboard-desc">
            Register a new custom token on the Morpheum platform. You need to
            sign the request with your connected wallet. After onboarding, use the
            Bank Service to transfer tokens.
          </p>

          <div className="form-group">
            <label htmlFor="ct-issuer">Issuer address</label>
            <input
              id="ct-issuer"
              type="text"
              value={issuerAddress}
              readOnly
              className="read-only"
              aria-describedby="ct-issuer-hint"
            />
            <span id="ct-issuer-hint" className="field-hint">
              From connected wallet
            </span>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="ct-ticker">
                Ticker <span className="required">*</span>
              </label>
              <input
                id="ct-ticker"
                type="text"
                value={ticker}
                onChange={(e) => setTicker(e.target.value)}
                placeholder="e.g. CTK"
                maxLength={16}
                autoComplete="off"
              />
            </div>
            <div className="form-group">
              <label htmlFor="ct-decimals">Decimals</label>
              <input
                id="ct-decimals"
                type="number"
                min={0}
                max={18}
                value={decimals}
                onChange={(e) =>
                  setDecimals(
                    Math.min(18, Math.max(0, Number(e.target.value) || 0))
                  )
                }
              />
              <span className="field-hint">Default 8 (satoshi format)</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="ct-name">Name</label>
            <input
              id="ct-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Custom Token"
            />
          </div>

          <div className="form-group">
            <label htmlFor="ct-initial-supply">Initial supply (tokens)</label>
            <input
              id="ct-initial-supply"
              type="text"
              inputMode="decimal"
              value={initialSupplyTokens}
              onChange={(e) => setInitialSupplyTokens(e.target.value)}
              placeholder="e.g. 1000000"
            />
            <span className="field-hint">
              Optional. Stored in satoshi (×10<sup>{decimals}</sup>).
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="ct-description">Description</label>
            <textarea
              id="ct-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description of the token"
              rows={2}
            />
          </div>

          <div className="form-group">
            <label htmlFor="ct-logo">Logo URL</label>
            <input
              id="ct-logo"
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://..."
            />
          </div>

          <p className="custom-token-onboard-note">
            All onboarding requests require EIP-712 signature verification.
            Rate limit: 10 requests per minute per address.
          </p>
          <p className="custom-token-onboard-fee">
            Fee: about {CUSTOM_TOKEN_ONBOARD_FEE_ABOUT_A} $MORM (Round A).
          </p>

          <div className="form-actions">
            {onCancel && (
              <button
                type="button"
                className="secondary"
                onClick={onCancel}
                disabled={submitting}
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="primary"
              disabled={submitting || !ticker.trim()}
            >
              {submitting ? "Onboarding…" : "Onboard token"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
